// The live connection signal: what the API client has observed, shared with
// whatever wants to show it.
//
// Deliberately dependency-free so the api client can feed it without an import
// cycle, and so the DECISION (connectionState.ts) stays pure and testable
// apart from the plumbing.
import { connectionState, countsAsConnectionFailure, type ConnectionState } from './connectionState';

type Listener = () => void;

let consecutiveFailures = 0;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((l) => l());

const deviceOnline = (): boolean => {
  try {
    return navigator.onLine !== false;
  } catch {
    // No navigator (tests, SSR): assume online rather than invent an outage.
    return true;
  }
};

/**
 * Record what happened to a request.
 *
 * ⚠️ Called for EVERY outcome, success included — a single success is proof
 * the connection works and must clear the count immediately. Without that, one
 * blip plus a later unrelated blip would eventually cross the threshold and
 * show a banner to somebody whose connection is fine.
 */
export const recordRequestOutcome = (status?: number | null): void => {
  const before = consecutiveFailures;
  if (status !== undefined && status !== null && status > 0 && !countsAsConnectionFailure(status)) {
    consecutiveFailures = 0;
  } else if (countsAsConnectionFailure(status)) {
    consecutiveFailures += 1;
  }
  if (consecutiveFailures !== before) notify();
};

/** A request got through. */
export const recordRequestSuccess = (): void => {
  if (consecutiveFailures !== 0) {
    consecutiveFailures = 0;
    notify();
  }
};

export const connectionSnapshot = (): ConnectionState =>
  connectionState({ deviceOnline: deviceOnline(), consecutiveFailures });

export const subscribeToConnection = (fn: Listener): (() => void) => {
  listeners.add(fn);
  // The browser's own events are part of the signal: going offline should show
  // immediately rather than waiting for a request to fail.
  const on = () => notify();
  try {
    window.addEventListener('online', on);
    window.addEventListener('offline', on);
  } catch { /* no window */ }
  return () => {
    listeners.delete(fn);
    try {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', on);
    } catch { /* no window */ }
  };
};

/** Tests only. */
export const __resetConnection = () => { consecutiveFailures = 0; notify(); };
